import React, { useEffect, useState } from 'react';
import axios, { apiBaseUrl } from "../../api/axios";
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/Loader';
import ResponsiveTable from "../../components/ResponsiveTable";
import ExportButtons from "../../components/ExportButtons";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

function Items() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/items');
      setItems(res.data || []);
    } catch (err) {
      console.error('Fetch items error:', err);
      toast.error(err?.response?.data?.error || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDownload = (type) => {
    const url = `${apiBaseUrl}/items/export/${type}`;
    window.open(url, "_blank");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`/items/${id}`);
      toast.success("Item deleted.");
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete item');
    }
  };

  const actions = [
    { label: 'Edit', value: 'edit', icon: <PencilIcon className="w-4 h-4 inline" /> },
    { divider: true },
    { label: 'Delete', value: 'delete', icon: <TrashIcon className="w-4 h-4 inline text-red-600" /> },
  ];

  const handleActionClick = (action, row) => {
    switch (action) {
      case "edit":
        navigate(`/item/edit/${row.id}`);
        break;
      case "delete":
        handleDelete(row.id);
        break;
      default:
        break;
    }
  };

  const columns = [
    { header: "SL", accessor: "sl", render: (v, row, i) => i + 1 },
    { header: "Invoice#", accessor: "sku" },
    { header: "Item Name", accessor: "name" },
    { header: "Category", accessor: "category_name" },
    { header: "Brand", accessor: "brand_name" },
    { header: "Unit", accessor: "unit" },
    {
      header: "Sizes",
      accessor: "sizes",
      render: (value) => {
        if (!value || !value.length) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((s, i) => (
              <span
                key={`${s.size}-${i}`}
                className="inline-flex items-center gap-1 rounded border px-2 py-0.5"
              >
                <span className="font-medium">{s.size}</span>
                <span className="text-gray-600">({s.quantity})</span>
              </span>
            ))}
          </div>
        );
      }
    },
    {
      header: "Colors",
      accessor: "colors",
      render: (value) => {
        if (!value || !value.length) return <span className="text-gray-400">-</span>;
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((c, i) => (
              <span
                key={`${c.color}-${i}`}
                className="inline-flex items-center gap-1 rounded border px-2 py-0.5"
              >
                <span className="font-medium">{c.color}</span>
                <span className="text-gray-600">({c.quantity})</span>
              </span>
            ))}
          </div>
        );
      }
    },
    { header: "T. Qty", accessor: "total_quantity" },
    { header: "Prev P.", accessor: "purchase_price_prev" },
    { header: "P. Price", accessor: "purchase_price" },
    { header: "Avg P.", accessor: "purchase_price_avg" },
    { header: "Sales P.", accessor: "sales_price" },
  ];

  return (
    <div className="relative flex">
      <div className="p-4 w-full">
        <ExportButtons title="Products List" onDownload={handleDownload} />
        <ResponsiveTable
          columns={columns}
          data={items}
          showActions
          actionButtons={actions}
          onActionClick={handleActionClick}
        />
      </div>
      <Loader show={loading} />
    </div>
  );
}

export default Items;